import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { readableTextColor } from '../../../utils/readableTextColor';
import { Editable } from '../inline/Editable';

interface SectionHeaderProps {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  align?: 'left' | 'center' | 'right';
  titleClassName?: string;
  subtitleClassName?: string;
  /**
   * Inline-edit field paths. Nearly every section stores its header as
   * config.title / config.subtitle, so these defaults make the header
   * click-to-editable across all of them at once. Pass a different path (or
   * undefined) for the rare section whose header isn't config-backed.
   */
  titleField?: string;
  subtitleField?: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  subtitle,
  align = 'center',
  titleClassName = 'text-3xl sm:text-4xl md:text-5xl font-bold',
  subtitleClassName = 'text-base sm:text-lg',
  titleField = 'config.title',
  subtitleField = 'config.subtitle',
}) => {
  // Solid-primary eyebrow pill needs a readable text colour. CSS
  // --primary-color can be overridden at the section level (e.g. a
  // section uses fuchsia when the template default is purple), so
  // reading html-level CSS or the config primary gives wrong answer.
  // Use a ref + useLayoutEffect to read the ACTUAL cascaded background
  // colour from the rendered DOM, then derive text. useLayoutEffect
  // fires before paint → Lighthouse sees the right colour.
  const eyebrowRef = useRef<HTMLSpanElement>(null);
  const [eyebrowTextColor, setEyebrowTextColor] = useState<'#fff' | '#111'>('#fff');
  useLayoutEffect(() => {
    if (!eyebrowRef.current) return;
    const bg = getComputedStyle(eyebrowRef.current).backgroundColor;
    setEyebrowTextColor(readableTextColor(bg));
  }, [eyebrow]);
  useEffect(() => {
    if (!eyebrowRef.current) return;
    const bg = getComputedStyle(eyebrowRef.current).backgroundColor;
    setEyebrowTextColor(readableTextColor(bg));
  }, [eyebrow]);

  if (!title && !subtitle && !eyebrow) return null;
  const alignClass = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
  return (
    <div className={`mb-10 ${alignClass}`}>
      {eyebrow && (
        <span
          ref={eyebrowRef}
          className="inline-block text-sm font-semibold tracking-[0.14em] uppercase mb-3 px-3.5 py-1.5 rounded-full"
          style={{
            backgroundColor: 'var(--primary-color, #007AFF)',
            color: eyebrowTextColor,
          }}
        >
          {eyebrow}
        </span>
      )}
      {title && (
        <Editable
          as="h2"
          field={titleField}
          value={title}
          className={`${titleClassName} break-words`}
          style={{
            fontFamily: 'var(--section-title-font-family, var(--font-heading))',
            fontSize: 'var(--section-title-font-size)',
            fontWeight: 'var(--section-title-font-weight, 700)',
            // Display headings want to be set tight — slight negative tracking so
            // big section titles read deliberate, not just enlarged body type.
            letterSpacing: 'var(--section-title-letter-spacing, -0.015em)',
            color: 'var(--header-text-color, var(--color-text, #111827))',
          }}
        />
      )}
      {subtitle && (
        <Editable
          as="p"
          field={subtitleField}
          value={subtitle}
          className={`${subtitleClassName} mt-3 max-w-2xl mx-auto break-words whitespace-pre-line`}
          style={{
            fontFamily: 'var(--section-subtitle-font-family, var(--font-primary))',
            fontSize: 'var(--section-subtitle-font-size)',
            color: 'var(--color-text, #374151)',
            opacity: 0.85,
          }}
        />
      )}
    </div>
  );
};
